// src/pages/CRM/Core/LeadDetail.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  IconButton,
  Chip,
  Avatar,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
  Breadcrumbs,
  Link,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  LinearProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  LocationOn as LocationIcon,
  Language as WebsiteIcon,
  Add as AddIcon,
  Note as NoteIcon,
  Event as MeetingIcon,
  PhoneCallback as CallIcon,
  Person as PersonIcon,
  SwapHoriz as ConvertIcon,
  Whatshot as HotIcon,
  AcUnit as ColdIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
  LeadStatus,
  LeadStatusLabels,
  LeadStatusColors,
  LeadScore,
  LeadScoreLabels,
  LeadScoreColors,
  LeadSource,
  LeadSourceLabels,
  ActivityType,
  ActivityTypeLabels,
} from './crmEnums';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`lead-tabpanel-${index}`}
      aria-labelledby={`lead-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const LeadDetail = () => {
  const navigate = useNavigate();
  const { chatbotId, leadId } = useParams();
  const [searchParams] = useSearchParams();
  const initialEditMode = searchParams.get('edit') === 'true';

  // Permission checks
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // State
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(initialEditMode);
  const [tabValue, setTabValue] = useState(0);

  // Form state
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [activityForm, setActivityForm] = useState({
    activity_type: ActivityType.NOTE,
    subject: '',
    description: '',
    activity_date: new Date().toISOString().slice(0, 16),
  });
  const [convertForm, setConvertForm] = useState({ create_account: true });
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    checkPermissions();
  }, []);

  const checkPermissions = async () => {
    const view = await hasPermission('crm', 'view');
    const edit = await hasPermission('crm', 'edit');
    const del = await hasPermission('crm', 'delete');
    setCanView(view);
    setCanEdit(edit);
    setCanDelete(del);
  };

  // Fetch lead details
  const fetchLead = useCallback(async () => {
    if (!canView) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(`/api/crm/leads/${leadId}`);

      if (response.data.success) {
        const leadData = response.data.data;
        setLead(leadData);
        setFormData({
          prefix: leadData.prefix || '',
          first_name: leadData.first_name || '',
          last_name: leadData.last_name || '',
          email: leadData.email || '',
          phone: leadData.phone || '',
          phone_mobile: leadData.phone_mobile || '',
          company_name: leadData.company_name || '',
          job_title: leadData.job_title || '',
          industry: leadData.industry || '',
          website: leadData.website || '',
          address_street: leadData.address?.street || '',
          address_city: leadData.address?.city || '',
          address_state: leadData.address?.state || '',
          address_postal_code: leadData.address?.postal_code || '',
          address_country: leadData.address?.country || '',
          status: leadData.status || LeadStatus.NEW,
          score: leadData.score || LeadScore.COLD,
          source: leadData.source || LeadSource.MANUAL_ENTRY,
          description: leadData.description || '',
          qualification_notes: leadData.qualification_notes || '',
          email_opt_out: leadData.email_opt_out || false,
          sms_opt_out: leadData.sms_opt_out || false,
          do_not_call: leadData.do_not_call || false,
          tags: leadData.tags || [],
        });
      } else {
        setError(response.data.message || 'Failed to fetch lead');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch lead');
    } finally {
      setLoading(false);
    }
  }, [canView, leadId]);

  useEffect(() => {
    if (canView) {
      fetchLead();
    }
  }, [fetchLead, canView]);

  // Handlers
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFormChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.first_name?.trim()) {
      errors.first_name = 'First name is required';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const response = await axiosInstance.put(`/api/crm/leads/${leadId}`, formData);

      if (response.data.success) {
        setEditMode(false);
        fetchLead();
      } else {
        setFormErrors({ submit: response.data.message || 'Failed to update lead' });
      }
    } catch (err) {
      setFormErrors({
        submit: err.response?.data?.message || 'Failed to update lead',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    fetchLead();
    setFormErrors({});
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await axiosInstance.delete(`/api/crm/leads/${leadId}`);

      if (response.data.success) {
        navigate(`/chatbot/${chatbotId}/crm/leads`);
      } else {
        setError(response.data.message || 'Failed to delete lead');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete lead');
    }
    setDeleteDialogOpen(false);
  };

  const handleConvertConfirm = async () => {
    setConverting(true);
    try {
      const response = await axiosInstance.post(`/api/crm/leads/${leadId}/convert`, convertForm);

      if (response.data.success) {
        setConvertDialogOpen(false);
        if (response.data.data?.contact?.id) {
          navigate(`/chatbot/${chatbotId}/crm/contacts/${response.data.data.contact.id}`);
        } else {
          navigate(`/chatbot/${chatbotId}/crm/leads`);
        }
      } else {
        setError(response.data.message || 'Failed to convert lead');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to convert lead');
    } finally {
      setConverting(false);
    }
  };

  // Activity handlers
  const handleActivityFormChange = (field) => (event) => {
    setActivityForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleAddActivity = async () => {
    try {
      const response = await axiosInstance.post('/api/crm/activities', {
        ...activityForm,
        lead_id: parseInt(leadId),
      });

      if (response.data.success) {
        setActivityDialogOpen(false);
        setActivityForm({
          activity_type: ActivityType.NOTE,
          subject: '',
          description: '',
          activity_date: new Date().toISOString().slice(0, 16),
        });
        fetchLead();
      }
    } catch (err) {
      console.error('Failed to add activity:', err);
    }
  };

  // Helper functions
  const getFullName = () => {
    if (!lead) return '';
    return [lead.prefix, lead.first_name, lead.last_name].filter(Boolean).join(' ');
  };

  const getInitials = () => {
    if (!lead) return '?';
    const first = lead.first_name?.[0] || '';
    const last = lead.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  const getScoreIcon = (score) => {
    if (score === LeadScore.HOT) return <HotIcon color="error" />;
    if (score === LeadScore.COLD) return <ColdIcon color="info" />;
    return null;
  };

  const getActivityIcon = (type) => {
    const icons = {
      [ActivityType.EMAIL]: <EmailIcon />,
      [ActivityType.CALL]: <CallIcon />,
      [ActivityType.MEETING]: <MeetingIcon />,
      [ActivityType.NOTE]: <NoteIcon />,
    };
    return icons[type] || <NoteIcon />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3, mt: 10 }}>
        <Alert severity="error">You do not have permission to view this lead.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, mt: 10, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, mt: 10 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/crm/leads`)}
          sx={{ mt: 2 }}
        >
          Back to Leads
        </Button>
      </Box>
    );
  }

  if (!lead) {
    return (
      <Box sx={{ p: 3, mt: 10 }}>
        <Alert severity="warning">Lead not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/crm/leads`)}
          sx={{ mt: 2 }}
        >
          Back to Leads
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/chatbot/${chatbotId}/crm`)}
          >
            CRM
          </Link>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/chatbot/${chatbotId}/crm/leads`)}
          >
            Leads
          </Link>
          <Typography color="text.primary">{getFullName()}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/leads`)}>
              <ArrowBackIcon />
            </IconButton>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor:
                  lead.score === LeadScore.HOT
                    ? 'error.main'
                    : lead.score === LeadScore.COLD
                    ? 'info.main'
                    : 'warning.main',
                fontSize: '1.5rem',
              }}
            >
              {getInitials()}
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1">
                {getFullName()}
              </Typography>
              {lead.company_name && (
                <Typography variant="subtitle1" color="text.secondary">
                  {lead.job_title ? `${lead.job_title} at ` : ''}
                  {lead.company_name}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Chip
                  label={LeadStatusLabels[lead.status]}
                  size="small"
                  color={LeadStatusColors[lead.status]}
                />
                <Chip
                  icon={getScoreIcon(lead.score)}
                  label={LeadScoreLabels[lead.score]}
                  size="small"
                  color={LeadScoreColors[lead.score]}
                  variant="outlined"
                />
                {lead.source && (
                  <Chip
                    icon={<CampaignIcon />}
                    label={LeadSourceLabels[lead.source]}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Stack direction="row" spacing={1}>
            {editMode ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                {canEdit && !lead.is_converted && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => setEditMode(true)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<ConvertIcon />}
                      onClick={() => setConvertDialogOpen(true)}
                    >
                      Convert
                    </Button>
                  </>
                )}
                {canDelete && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Delete
                  </Button>
                )}
              </>
            )}
          </Stack>
        </Box>
      </Box>

      {formErrors.submit && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setFormErrors({})}>
          {formErrors.submit}
        </Alert>
      )}

      {lead.is_converted && (
        <Alert severity="info" sx={{ mb: 3 }}>
          This lead has been converted on {formatDate(lead.converted_at)}.
          <Button
            size="small"
            onClick={() => navigate(`/chatbot/${chatbotId}/crm/contacts/${lead.converted_contact_id}`)}
            sx={{ ml: 2 }}
          >
            View Contact
          </Button>
        </Alert>
      )}

      {/* Score Progress */}
      {lead.score_numeric !== undefined && lead.score_numeric !== null && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle2">Lead Score:</Typography>
            <Box sx={{ flexGrow: 1, maxWidth: 300 }}>
              <LinearProgress
                variant="determinate"
                value={lead.score_numeric}
                color={lead.score_numeric >= 70 ? 'error' : lead.score_numeric >= 40 ? 'warning' : 'info'}
                sx={{ height: 10, borderRadius: 5 }}
              />
            </Box>
            <Typography variant="body2" fontWeight="bold">
              {lead.score_numeric}/100
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Details" />
          <Tab label="Activities" />
        </Tabs>
      </Paper>

      {/* Details Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="First Name"
                      value={formData.first_name}
                      onChange={handleFormChange('first_name')}
                      error={!!formErrors.first_name}
                      helperText={formErrors.first_name}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Last Name"
                      value={formData.last_name}
                      onChange={handleFormChange('last_name')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={handleFormChange('email')}
                      error={!!formErrors.email}
                      helperText={formErrors.email}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Phone"
                      value={formData.phone}
                      onChange={handleFormChange('phone')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Mobile"
                      value={formData.phone_mobile}
                      onChange={handleFormChange('phone_mobile')}
                    />
                  </Grid>
                </Grid>
              ) : (
                <List dense>
                  {lead.email && (
                    <ListItem>
                      <ListItemIcon><EmailIcon /></ListItemIcon>
                      <ListItemText primary={lead.email} secondary="Email" />
                    </ListItem>
                  )}
                  {lead.phone && (
                    <ListItem>
                      <ListItemIcon><PhoneIcon /></ListItemIcon>
                      <ListItemText primary={lead.phone} secondary="Phone" />
                    </ListItem>
                  )}
                  {lead.phone_mobile && (
                    <ListItem>
                      <ListItemIcon><PhoneIcon /></ListItemIcon>
                      <ListItemText primary={lead.phone_mobile} secondary="Mobile" />
                    </ListItem>
                  )}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Company Information */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Company Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Company Name"
                      value={formData.company_name}
                      onChange={handleFormChange('company_name')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Job Title"
                      value={formData.job_title}
                      onChange={handleFormChange('job_title')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Industry"
                      value={formData.industry}
                      onChange={handleFormChange('industry')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Website"
                      value={formData.website}
                      onChange={handleFormChange('website')}
                    />
                  </Grid>
                </Grid>
              ) : (
                <List dense>
                  {lead.company_name && (
                    <ListItem>
                      <ListItemIcon><BusinessIcon /></ListItemIcon>
                      <ListItemText primary={lead.company_name} secondary="Company" />
                    </ListItem>
                  )}
                  {lead.job_title && (
                    <ListItem>
                      <ListItemIcon><PersonIcon /></ListItemIcon>
                      <ListItemText primary={lead.job_title} secondary="Job Title" />
                    </ListItem>
                  )}
                  {lead.industry && (
                    <ListItem>
                      <ListItemIcon><BusinessIcon /></ListItemIcon>
                      <ListItemText primary={lead.industry} secondary="Industry" />
                    </ListItem>
                  )}
                  {lead.website && (
                    <ListItem button component="a" href={lead.website} target="_blank">
                      <ListItemIcon><WebsiteIcon /></ListItemIcon>
                      <ListItemText primary={lead.website} secondary="Website" />
                    </ListItem>
                  )}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Lead Management */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Lead Management
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={formData.status}
                        label="Status"
                        onChange={handleFormChange('status')}
                      >
                        {Object.values(LeadStatus).map((status) => (
                          <MenuItem key={status} value={status}>
                            {LeadStatusLabels[status]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Score</InputLabel>
                      <Select
                        value={formData.score}
                        label="Score"
                        onChange={handleFormChange('score')}
                      >
                        {Object.values(LeadScore).map((score) => (
                          <MenuItem key={score} value={score}>
                            {LeadScoreLabels[score]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Source</InputLabel>
                      <Select
                        value={formData.source}
                        label="Source"
                        onChange={handleFormChange('source')}
                      >
                        {Object.values(LeadSource).map((source) => (
                          <MenuItem key={source} value={source}>
                            {LeadSourceLabels[source]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Qualification Notes"
                      value={formData.qualification_notes}
                      onChange={handleFormChange('qualification_notes')}
                    />
                  </Grid>
                </Grid>
              ) : (
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary={LeadStatusLabels[lead.status]}
                      secondary="Status"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={LeadScoreLabels[lead.score]}
                      secondary="Score"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={LeadSourceLabels[lead.source]}
                      secondary="Source"
                    />
                  </ListItem>
                  {lead.qualification_notes && (
                    <ListItem>
                      <ListItemText
                        primary={lead.qualification_notes}
                        secondary="Qualification Notes"
                      />
                    </ListItem>
                  )}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Description */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Description
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={handleFormChange('description')}
                  placeholder="Add notes about this lead..."
                />
              ) : (
                <Typography variant="body2" color={lead.description ? 'text.primary' : 'text.secondary'}>
                  {lead.description || 'No description'}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Activities Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Activity Timeline</Typography>
          {canEdit && !lead.is_converted && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setActivityDialogOpen(true)}
            >
              Log Activity
            </Button>
          )}
        </Box>

        {lead.recent_activities?.length > 0 ? (
          <List>
            {lead.recent_activities.map((activity) => (
              <ListItem key={activity.id} alignItems="flex-start" divider>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {getActivityIcon(activity.activity_type)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1">{activity.subject}</Typography>
                      <Chip
                        label={ActivityTypeLabels[activity.activity_type]}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {activity.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {formatDate(activity.activity_date)}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No activities recorded</Typography>
            {canEdit && !lead.is_converted && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => setActivityDialogOpen(true)}
                sx={{ mt: 2 }}
              >
                Log First Activity
              </Button>
            )}
          </Paper>
        )}
      </TabPanel>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Lead</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{getFullName()}</strong>?
            This action will disqualify the lead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert Lead Dialog */}
      <Dialog open={convertDialogOpen} onClose={() => setConvertDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Convert Lead to Contact</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Converting <strong>{getFullName()}</strong> will create a new Contact record.
          </Alert>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Create Account?</InputLabel>
            <Select
              value={convertForm.create_account}
              label="Create Account?"
              onChange={(e) => setConvertForm((prev) => ({ ...prev, create_account: e.target.value }))}
            >
              <MenuItem value={true}>
                Yes - Create a new Account from "{lead.company_name || 'Company'}"
              </MenuItem>
              <MenuItem value={false}>No - Contact only</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)} disabled={converting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConvertConfirm}
            disabled={converting}
            startIcon={converting ? <CircularProgress size={20} /> : <ConvertIcon />}
          >
            Convert Lead
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Activity Dialog */}
      <Dialog open={activityDialogOpen} onClose={() => setActivityDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Activity</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Activity Type</InputLabel>
                <Select
                  value={activityForm.activity_type}
                  label="Activity Type"
                  onChange={handleActivityFormChange('activity_type')}
                >
                  {Object.values(ActivityType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {ActivityTypeLabels[type]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={activityForm.subject}
                onChange={handleActivityFormChange('subject')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={activityForm.description}
                onChange={handleActivityFormChange('description')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Date & Time"
                value={activityForm.activity_date}
                onChange={handleActivityFormChange('activity_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivityDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddActivity} disabled={!activityForm.subject}>
            Log Activity
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadDetail;